import usePrivateLink from "../../config/usePrivateLink";
import React, { useEffect, useState } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import * as XLSX from "xlsx";

import { getTransitionDetails } from "../../../api/ReportsServices";

import { ToastMain, SweetAlertShow, LoadingDiv } from "../../master/Master";

import { getSessions } from "../../../api/SessionsService";
import { BankList } from "../../../api/BankService";
import { printCreditDebitReport } from "./PrintReports";
import ReportLayout from "./ReportLayout";

export default function CreditDebit() {
  const hasAccess = usePrivateLink();

  const [page, setPage] = useState(1);
  const [Data, setData] = useState([]);
  const [nav, setNav] = useState({});

  // Credit / Debit totals
  const [feetotal, setFeeTotal] = useState(0);
  const [totalexpense, setExpenseTotle] = useState(0);

  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedSession, setSelectedSessions] = useState("");
  const [sessions, setSessions] = useState([]);

  const [type, setType] = useState("");
  const [banks, setBanks] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");

  const [filterCount, setFilterCount] = useState(0);

  const getDefaultDates = () => {
    const today = new Date();

    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date) => {
      if (!(date instanceof Date)) {
        date = new Date(date);
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(firstDayOfMonth),
      to: formatDate(today),
    };
  };

  const { from: defaultFrom, to: defaultTo } = getDefaultDates();

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  /*
  |--------------------------------------------------------------------------
  | HELPERS
  |--------------------------------------------------------------------------
  */

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-IN");
  };

  const getTransactionTypeLabel = (item) => {
    if (item?.type === "fee") {
      return "Credit";
    }

    if (item?.type === "expense") {
      return "Debit";
    }

    return "-";
  };

  const isCreditTransaction = (item) => {
    return item?.type?.toLowerCase() === "fee";
  };

  const isDebitTransaction = (item) => {
    return item?.type?.toLowerCase() === "expense";
  };

  /*
  |--------------------------------------------------------------------------
  | GET PERSON NAME
  |--------------------------------------------------------------------------
  */

  const getPersonName = (item) => {
    if (!item) return "-";

    const person = item.person || {};

    return (
      person.name ||
      item.student?.name ||
      item.hostel_student?.name ||
      item.library_student?.name ||
      item.bus_student?.name ||
      item.staff?.name ||
      "-"
    );
  };

  /*
  |--------------------------------------------------------------------------
  | GET REGISTRATION / IDENTIFICATION NUMBER
  |--------------------------------------------------------------------------
  */

  const getRegistrationNumber = (item) => {
    if (!item) return "";

    const person = item.person || {};

    return person.registration_no || item.student?.registration_no || "";
  };

  /*
  |--------------------------------------------------------------------------
  | GET MODULE
  |--------------------------------------------------------------------------
  */

  const getModuleLabel = (item) => {
    if (!item) return "-";

    if (item.module_label) {
      return item.module_label;
    }

    if (Array.isArray(item.module_names) && item.module_names.length) {
      return item.module_names.join(", ");
    }

    if (Array.isArray(item.module)) {
      return item.module
        .map((module) => module?.type)
        .filter(Boolean)
        .join(", ");
    }

    return item.type === "expense" ? "Expense" : "Fee Payment";
  };

  /*
  |--------------------------------------------------------------------------
  | GET SOURCE
  |--------------------------------------------------------------------------
  */

  const getSourceLabel = (item) => {
    if (item?.source_label) {
      return item.source_label;
    }

    if (item?.type === "expense") {
      return "Expense";
    }

    return "Student";
  };

  /*
  |--------------------------------------------------------------------------
  | GET BANK ACCOUNT
  |--------------------------------------------------------------------------
  */

  const getBankAccount = (item) => {
    return item?.bank?.account_no || item?.account_no || "-";
  };

  /*
  |--------------------------------------------------------------------------
  | GET BANK NAME / OWNER
  |--------------------------------------------------------------------------
  */

  const getBankName = (item) => {
    return item?.bank?.owner_name || item?.bank?.name || "-";
  };

  /*
  |--------------------------------------------------------------------------
  | GET TRANSACTION REFERENCE
  |--------------------------------------------------------------------------
  */

  const getTransactionReference = (item) => {
    if (!item) return "-";

    const values = [];

    if (item.receipt_no) {
      values.push(item.receipt_no);
    }

    if (item.transaction_no) {
      values.push(item.transaction_no);
    }

    return values.length ? values.join(" • ") : "-";
  };

  /*
  |--------------------------------------------------------------------------
  | BANK LIST
  |--------------------------------------------------------------------------
  */

  const getBusinessBanks = async () => {
    setLoading(true);

    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to fetch bank accounts.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | SESSION LIST
  |--------------------------------------------------------------------------
  */

  const getSession = async () => {
    setLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionList = response?.data?.data || [];

        setSessions(sessionList);

        const activeSession = sessionList.find(
          (session) => session.status === "1",
        );

        if (activeSession) {
          setSelectedSessions(activeSession.id);
        }
      } else {
        SweetAlertShow("info", "Warning", "Failed to fetch sessions.");
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message || "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBusinessBanks();
    getSession();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | BUILD API URL
  |--------------------------------------------------------------------------
  */

  const buildFilterUrl = (includePage = true) => {
    let addUrl = "";

    if (includePage) {
      addUrl += `&page=${page}`;
    }

    if (selectedSession) {
      addUrl += `&session_id=${selectedSession}`;
    }

    if (from) {
      addUrl += `&start_date=${from}`;
    }

    if (to) {
      addUrl += `&end_date=${to}`;
    }

    if (name) {
      addUrl += `&search=${encodeURIComponent(name)}`;
    }

    /*
     * Keep existing backend values:
     *
     * fee     = Credit
     * expense = Debit
     */
    if (type) {
      addUrl += `&type=${type}`;
    }

    if (selectedAccount) {
      addUrl += `&bank_id=${selectedAccount}`;
    }

    return addUrl;
  };

  /*
  |--------------------------------------------------------------------------
  | FETCH REPORT
  |--------------------------------------------------------------------------
  */

  const fetchData = async () => {
    if (!selectedSession) return;

    setLoading(true);

    try {
      const addUrl = buildFilterUrl(true);

      const response = await getTransitionDetails(addUrl);

      const responseData = response?.data || {};

      setData(Array.isArray(responseData.data) ? responseData.data : []);

      setNav(responseData);

      /*
       * New API keys
       * with old API fallback
       */
      setFeeTotal(
        Number(responseData.total_credit ?? responseData.total_fee ?? 0),
      );

      setExpenseTotle(
        Number(responseData.total_debit ?? responseData.total_expense ?? 0),
      );
    } catch (error) {
      console.error("Failed to fetch credit/debit report:", error);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch report.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedSession) return;

    fetchData();
  }, [page, from, to, name, selectedSession, type, selectedAccount]);

  /*
  |--------------------------------------------------------------------------
  | RESET PAGE WHEN FILTER CHANGES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    setPage(1);
  }, [from, to, name, selectedSession, type, selectedAccount]);

  /*
  |--------------------------------------------------------------------------
  | EXPORT
  |--------------------------------------------------------------------------
  */

  const handleExportAll = async () => {
    try {
      setLoading(true);

      const addUrl = `${buildFilterUrl(false)}&print_all=1`;

      const response = await getTransitionDetails(addUrl);

      const responseData = response?.data || {};

      const allData = Array.isArray(responseData.data) ? responseData.data : [];

      const totalCredit = Number(
        responseData.total_credit ?? responseData.total_fee ?? 0,
      );

      const totalDebit = Number(
        responseData.total_debit ?? responseData.total_expense ?? 0,
      );

      if (!allData.length) {
        SweetAlertShow(
          "info",
          "No Data",
          "No transactions found for the selected filters.",
        );

        return;
      }

      /*
      |--------------------------------------------------------------------------
      | DETAIL DATA
      |--------------------------------------------------------------------------
      */

      const formattedData = allData.map((item) => {
        const person = item?.person || {};

        return {
          Date: formatDate(item.date || item.created_at),

          Type: getTransactionTypeLabel(item),

          Source: getSourceLabel(item),

          "Transaction / Module": getModuleLabel(item),

          "Person Name": getPersonName(item),

          "Registration No": getRegistrationNumber(item),

          "Receipt No": item.receipt_no || "",

          "Transaction No": item.transaction_no || "",

          "Payment Method": item.payment_method || "",

          Amount: Number(item.amount || 0),

          "Bank Account": getBankAccount(item),

          "Bank / Account Holder": getBankName(item),

          Remark: item.note || "",

          "Mobile Number": person.phone || person.secondary_mobile || "",

          "Father Name": person.father_name || "",

          "Mother Name": person.mother_name || "",

          District: person.district || "",

          State: person.state || "",

          Village: person.village || "",

          Pincode: person.zipcode || "",
        };
      });

      /*
      |--------------------------------------------------------------------------
      | WORKBOOK
      |--------------------------------------------------------------------------
      */

      const workbook = XLSX.utils.book_new();

      /*
      |--------------------------------------------------------------------------
      | SUMMARY SHEET
      |--------------------------------------------------------------------------
      */

      const summaryData = [
        {
          "Total Credit": totalCredit,

          "Total Debit": totalDebit,

          "Net Balance": totalCredit - totalDebit,

          "Filtered Records": allData.length,

          "From Date": from || "",

          "To Date": to || "",

          Session:
            sessions?.find((x) => x.id == selectedSession)?.session || "",

          Type:
            type === "fee" ? "Credit" : type === "expense" ? "Debit" : "All",
        },
      ];

      const summarySheet = XLSX.utils.json_to_sheet(summaryData);

      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      /*
      |--------------------------------------------------------------------------
      | DETAIL SHEET
      |--------------------------------------------------------------------------
      */

      const detailSheet = XLSX.utils.json_to_sheet(formattedData);

      XLSX.utils.book_append_sheet(
        workbook,
        detailSheet,
        "Credit Debit Ledger",
      );

      /*
      |--------------------------------------------------------------------------
      | DOWNLOAD
      |--------------------------------------------------------------------------
      */

      XLSX.writeFile(workbook, "Credit_Debit_Ledger.xlsx");

      ToastMain.fire({
        icon: "success",
        title: `Exported ${allData.length} records successfully!`,
      });
    } catch (error) {
      console.error("Credit/Debit export error:", error);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Error exporting data.",
      );
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | PRINT
  |--------------------------------------------------------------------------
  */

  const handlePrint = async () => {
    try {
      setLoading(true);

      const addUrl = `${buildFilterUrl(false)}&print_all=1`;

      const response = await getTransitionDetails(addUrl);

      const responseData = response?.data || {};

      const records = Array.isArray(responseData.data) ? responseData.data : [];

      if (!records.length) {
        SweetAlertShow(
          "info",
          "No Data",
          "No transactions found for the selected filters.",
        );

        return;
      }

      const totalCredit = Number(
        responseData.total_credit ?? responseData.total_fee ?? 0,
      );

      const totalDebit = Number(
        responseData.total_debit ?? responseData.total_expense ?? 0,
      );

      /*
       * The print function receives the normalized
       * API records directly.
       */
      printCreditDebitReport({
        records,

        from,

        to,

        totalCredit,

        totalDebit,

        session: sessions?.find((x) => x.id == selectedSession)?.session || "",

        type: type === "fee" ? "Credit" : type === "expense" ? "Debit" : "All",
      });
    } catch (error) {
      console.error("Credit/Debit print error:", error);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to print report.",
      );
    } finally {
      setLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | FILTER COUNT
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let count = 0;

    if (selectedSession) count++;
    if (from) count++;
    if (to) count++;
    if (type) count++;
    if (selectedAccount) count++;

    setFilterCount(count);
  }, [selectedSession, from, to, type, selectedAccount]);

  /*
  |--------------------------------------------------------------------------
  | NET BALANCE
  |--------------------------------------------------------------------------
  */

  const netBalance = Number(feetotal || 0) - Number(totalexpense || 0);

  const selectedSessionName =
    sessions?.find((x) => x.id == selectedSession)?.session || "";

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid mt-2">
              <div className="row">
                <ReportLayout />

                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    {/* =====================================================
                        HEADER
                    ===================================================== */}

                    <div className="card-body">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                        <div>
                          <h3 className="fw-bold mb-2">
                            Credit / Debit Ledger
                          </h3>

                          <div className="d-flex flex-wrap gap-2">
                            {selectedSession && (
                              <span className="badge rounded-pill bg-label-primary">
                                <i className="ri-calendar-line me-1"></i>
                                Session: {selectedSessionName}
                              </span>
                            )}

                            {from && to && (
                              <span className="badge rounded-pill bg-label-secondary">
                                <i className="ri-calendar-2-line me-1"></i>
                                {from} → {to}
                              </span>
                            )}

                            {type && (
                              <span className="badge rounded-pill bg-label-info">
                                <i className="ri-exchange-line me-1"></i>
                                {type === "fee" ? "Credit" : "Debit"}
                              </span>
                            )}

                            {selectedAccount && (
                              <span className="badge rounded-pill bg-label-warning">
                                <i className="ri-bank-line me-1"></i>
                                Bank Selected
                              </span>
                            )}
                          </div>
                        </div>

                        {/* =================================================
                            ACTIONS
                        ================================================= */}

                        <div className="d-flex flex-wrap gap-2">
                          <div
                            className="input-group"
                            style={{
                              width: "300px",
                            }}
                          >
                            <span className="input-group-text">
                              <i className="ri-search-line"></i>
                            </span>

                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search name, receipt, transaction..."
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                            />
                          </div>

                          <button
                            className="btn btn-outline-primary"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#filterDrawer"
                          >
                            <i className="ri-filter-3-line me-1"></i>
                            Filters
                            {filterCount > 0 && (
                              <span className="badge rounded-pill bg-danger ms-1">
                                {filterCount}
                              </span>
                            )}
                          </button>

                          {hasAccess("/reports/credit/debit/export") && (
                            <button
                              className="btn btn-success"
                              onClick={handleExportAll}
                            >
                              <i className="ri-file-excel-2-line me-1"></i>
                              Export
                            </button>
                          )}

                          {hasAccess("/reports/credit/debit/print") && (
                            <button
                              className="btn btn-primary"
                              onClick={handlePrint}
                            >
                              <i className="ri-printer-line me-1"></i>
                              Print
                            </button>
                          )}
                        </div>
                      </div>

                      {/* =================================================
                          SUMMARY CARDS
                      ================================================= */}

                      <div className="row g-2 mt-3">
                        {/* CREDIT */}

                        <div className="col-md-3">
                          <div className="border rounded-3 px-3 py-2 h-100">
                            <div className="text-muted small">Total Credit</div>

                            <div className="fs-4 fw-bold text-success">
                              ₹{formatAmount(feetotal)}
                            </div>
                          </div>
                        </div>

                        {/* DEBIT */}

                        <div className="col-md-3">
                          <div className="border rounded-3 px-3 py-2 h-100">
                            <div className="text-muted small">Total Debit</div>

                            <div className="fs-4 fw-bold text-danger">
                              ₹{formatAmount(totalexpense)}
                            </div>
                          </div>
                        </div>

                        {/* NET */}

                        <div className="col-md-3">
                          <div className="border rounded-3 px-3 py-2 h-100">
                            <div className="text-muted small">Net Balance</div>

                            <div
                              className={`fs-4 fw-bold ${
                                netBalance >= 0 ? "text-success" : "text-danger"
                              }`}
                            >
                              ₹{formatAmount(netBalance)}
                            </div>
                          </div>
                        </div>

                        {/* TRANSACTIONS */}

                        <div className="col-md-3">
                          <div className="border rounded-3 px-3 py-2 h-100">
                            <div className="text-muted small">Transactions</div>

                            <div className="fs-4 fw-bold text-warning">
                              {nav?.total || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        TABLE
                    ===================================================== */}

                    <div className="card-body pt-0">
                      <div className="table-responsive">
                        <table className="table table-hover align-middle">
                          <thead
                            style={{
                              background: "#f8f9fa",
                              fontSize: "13px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            <tr className="text-center bg-light">
                              <th>#</th>

                              <th>Date</th>

                              <th>Type</th>

                              <th>Transaction</th>

                              <th>Party</th>

                              <th>Bank</th>

                              <th>Remark</th>

                              <th>Amount</th>
                            </tr>
                          </thead>

                          <tbody>
                            {!Array.isArray(Data) || Data.length === 0 ? (
                              <tr>
                                <td colSpan="8" className="text-center py-5">
                                  <div className="text-muted">
                                    <i className="ri-file-search-line fs-2 d-block mb-2"></i>
                                    No transactions found.
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              Data.map((item, index) => {
                                const isCredit = isCreditTransaction(item);

                                const isDebit = isDebitTransaction(item);

                                const amount = parseFloat(item.amount) || 0;

                                const rowNumber =
                                  ((nav?.current_page || 1) - 1) *
                                    (nav?.per_page || 50) +
                                  index +
                                  1;

                                return (
                                  <tr
                                    key={`${item.type}-${item.id}`}
                                    style={{
                                      cursor: "pointer",
                                    }}
                                  >
                                    {/* NUMBER */}

                                    <td className="text-center">{rowNumber}</td>

                                    {/* DATE */}

                                    <td className="text-center text-nowrap">
                                      <div className="fw-semibold">
                                        {formatDate(
                                          item.date || item.created_at,
                                        )}
                                      </div>
                                    </td>

                                    {/* TYPE */}

                                    <td className="text-center">
                                      {isCredit ? (
                                        <span className="badge rounded-pill bg-success">
                                          <i className="ri-arrow-down-line me-1"></i>
                                          Credit
                                        </span>
                                      ) : isDebit ? (
                                        <span className="badge rounded-pill bg-danger">
                                          <i className="ri-arrow-up-line me-1"></i>
                                          Debit
                                        </span>
                                      ) : (
                                        <span className="badge rounded-pill bg-secondary">
                                          -
                                        </span>
                                      )}
                                    </td>

                                    {/* TRANSACTION */}

                                    <td>
                                      <div className="fw-semibold">
                                        {getModuleLabel(item)}
                                      </div>

                                      <div className="small text-muted">
                                        {getTransactionReference(item)}
                                      </div>

                                      {item.payment_method && (
                                        <span className="badge bg-label-secondary mt-1">
                                          {item.payment_method}
                                        </span>
                                      )}
                                    </td>

                                    {/* PARTY */}

                                    <td>
                                      <div className="fw-semibold">
                                        {getPersonName(item)}
                                      </div>

                                      <div className="small text-muted">
                                        {getSourceLabel(item)}

                                        {getRegistrationNumber(item) && (
                                          <>
                                            {" • "}
                                            {getRegistrationNumber(item)}
                                          </>
                                        )}
                                      </div>
                                    </td>

                                    {/* BANK */}

                                    <td>
                                      <div className="fw-semibold">
                                        {getBankAccount(item)}
                                      </div>

                                      <small className="text-muted">
                                        {getBankName(item)}
                                      </small>
                                    </td>

                                    {/* REMARK */}

                                    <td>
                                      <span
                                        className={
                                          item.note ? "" : "text-muted"
                                        }
                                      >
                                        {item.note || "-"}
                                      </span>
                                    </td>

                                    {/* AMOUNT */}

                                    <td className="text-end text-nowrap">
                                      <span
                                        className={
                                          isCredit
                                            ? "text-success fw-bold"
                                            : "text-danger fw-bold"
                                        }
                                      >
                                        {isCredit ? "+" : "-"}₹
                                        {formatAmount(amount)}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>

                        {/* =================================================
                            PAGINATION
                        ================================================= */}

                        {nav?.total > 0 && (
                          <div className="d-flex justify-content-between align-items-center p-3 border-top">
                            <div className="text-muted small">
                              Showing{" "}
                              {Data.length
                                ? (nav.current_page - 1) * nav.per_page + 1
                                : 0}
                              {" - "}
                              {Math.min(
                                nav.current_page * nav.per_page,
                                nav.total,
                              )}
                              {" of "}
                              {nav.total}
                            </div>

                            <ul className="pagination pagination-sm mb-0">
                              {/* PREVIOUS */}

                              <li
                                className={`page-item ${
                                  nav?.current_page <= 1 ? "disabled" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => {
                                    if (nav?.current_page > 1) {
                                      setPage(nav.current_page - 1);
                                    }
                                  }}
                                >
                                  <i className="ri-arrow-left-s-line"></i>
                                </button>
                              </li>

                              {/* PAGES */}

                              {Array.from(
                                {
                                  length: nav?.last_page || 1,
                                },
                                (_, idx) => {
                                  const pageNumber = idx + 1;

                                  return (
                                    <li
                                      key={pageNumber}
                                      className={`page-item ${
                                        nav?.current_page === pageNumber
                                          ? "active"
                                          : ""
                                      }`}
                                    >
                                      <button
                                        className="page-link"
                                        onClick={() => setPage(pageNumber)}
                                      >
                                        {pageNumber}
                                      </button>
                                    </li>
                                  );
                                },
                              )}

                              {/* NEXT */}

                              <li
                                className={`page-item ${
                                  nav?.current_page >= nav?.last_page
                                    ? "disabled"
                                    : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => {
                                    if (nav?.current_page < nav?.last_page) {
                                      setPage(nav.current_page + 1);
                                    }
                                  }}
                                >
                                  <i className="ri-arrow-right-s-line"></i>
                                </button>
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* =====================================================
                        FILTER DRAWER
                    ===================================================== */}

                    <div
                      className="offcanvas offcanvas-end"
                      tabIndex="-1"
                      id="filterDrawer"
                    >
                      <div className="offcanvas-header">
                        <h5 className="offcanvas-title">
                          <i className="ri-filter-3-line me-2"></i>
                          Report Filters
                        </h5>

                        <button
                          type="button"
                          className="btn-close"
                          data-bs-dismiss="offcanvas"
                        />
                      </div>

                      <div className="offcanvas-body">
                        {/* SESSION */}

                        <div className="form-floating form-floating-outline mb-3">
                          <select
                            className="form-select"
                            value={selectedSession || ""}
                            onChange={(e) => {
                              setSelectedSessions(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">Select Session</option>

                            {sessions?.map((session) => (
                              <option key={session.id} value={session.id}>
                                {session.session}
                              </option>
                            ))}
                          </select>

                          <label>Session</label>
                        </div>

                        {/* FROM DATE */}

                        <div className="form-floating form-floating-outline mb-3">
                          <input
                            type="date"
                            className="form-control"
                            value={from}
                            onChange={(e) => {
                              setFrom(e.target.value);
                              setPage(1);
                            }}
                          />

                          <label>From Date</label>
                        </div>

                        {/* TO DATE */}

                        <div className="form-floating form-floating-outline mb-3">
                          <input
                            type="date"
                            className="form-control"
                            value={to}
                            onChange={(e) => {
                              setTo(e.target.value);
                              setPage(1);
                            }}
                          />

                          <label>To Date</label>
                        </div>

                        {/* TYPE */}

                        <div className="form-floating form-floating-outline mb-3">
                          <select
                            className="form-select"
                            value={type}
                            onChange={(e) => {
                              setType(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All Types</option>

                            <option value="fee">Credit</option>

                            <option value="expense">Debit</option>
                          </select>

                          <label>Transaction Type</label>
                        </div>

                        {/* BANK */}

                        <div className="form-floating form-floating-outline mb-3">
                          <select
                            className="form-select"
                            value={selectedAccount}
                            onChange={(e) => {
                              setSelectedAccount(e.target.value);
                              setPage(1);
                            }}
                          >
                            <option value="">All Accounts</option>

                            {banks?.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.account_no}
                              </option>
                            ))}
                          </select>

                          <label>Bank Account</label>
                        </div>

                        {/* RESET */}

                        <button
                          className="btn btn-outline-danger w-100"
                          onClick={() => {
                            setType("");

                            setSelectedAccount("");

                            setFrom(defaultFrom);

                            setTo(defaultTo);

                            setName("");

                            setPage(1);
                          }}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Reset Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>

        <div className="drag-target"></div>
      </div>
    </>
  );
}
